import type {
  AgentDto,
  AgentVersionDto,
  EndpointDto,
  RequestContextValues,
  Role,
} from '@agent-platform/shared';
import { hasRole, promptVariables, sessionVariables } from '@agent-platform/shared';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Brain, Pencil, SlidersHorizontal, Variable, Webhook } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { AgentChat } from '@/components/agent-chat';
import { expectedHeaders, HeadersEditor, useTestHeaders } from '@/components/agent-headers';
import { EmptyState } from '@/components/empty-state';
import { ErrorAlert } from '@/components/error-alert';
import { PageHeader } from '@/components/page-header';
import { RequestContextPicker } from '@/components/request-context-picker';
import { RequireTenant } from '@/components/require-tenant';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api } from '@/lib/api';
import { usePaged } from '@/lib/queries';
import { cn } from '@/lib/utils';
import { useMediaQuery } from '@/hooks/use-media-query';

/** A titled block of the Playground's settings panel, styled like the agent Config panel. */
function SettingsSection({
  icon: Icon,
  tint,
  title,
  children,
}: {
  icon: typeof Brain;
  tint: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="grid gap-2.5 px-4 py-3.5">
      <h3 className="flex items-center gap-2 text-sm font-medium">
        <Icon className={cn('size-4 shrink-0', tint)} aria-hidden />
        {title}
      </h3>
      {children}
    </section>
  );
}

/**
 * The chat for one agent, with what to send alongside it in a side panel: test values for prompt
 * variables and, when the agent has an endpoint, request headers run through that endpoint's
 * mapping. Narrow screens open the panel as a slide-over instead.
 */
function PlaygroundSession({
  tenantId,
  agent,
  version,
  selectedVersion,
}: {
  tenantId: string;
  agent: AgentDto;
  version: string;
  selectedVersion: AgentVersionDto | undefined;
}) {
  const [requestContext, setRequestContext] = useState<RequestContextValues | null>({});
  const [sheetOpen, setSheetOpen] = useState(false);
  const wide = useMediaQuery('(min-width: 1024px)');
  const endpoints = usePaged<EndpointDto>(['endpoints', tenantId], '/endpoints');
  const endpoint = endpoints.data?.pages
    .flatMap((p) => p.items)
    .find((e) => e.agentId === agent.id);
  const expected = expectedHeaders(endpoint);
  const testHeaders = useTestHeaders(agent.id, expected);
  // Thread memory or session levels: the server keeps the conversation.
  const stateful = Boolean(selectedVersion && selectedVersion.memoryMode !== 'stateless');
  // Prompt {{variables}} and the variables session levels are keyed by.
  const variables = [
    ...new Set([
      ...promptVariables(selectedVersion?.systemPrompt ?? ''),
      ...(selectedVersion?.memoryMode === 'sessions'
        ? sessionVariables(selectedVersion.sessionConfig.levels)
        : []),
    ]),
  ];

  const settings = (
    <div className="divide-y">
      <SettingsSection icon={Brain} tint="text-tint-violet" title="Memory">
        <p className="text-muted-foreground text-xs">
          {selectedVersion?.memoryMode === 'sessions'
            ? 'This version uses session levels: set their variables in Test values. Each reply shows the levels it used.'
            : stateful
              ? 'This version remembers the conversation. “New chat” starts a new thread.'
              : 'This version doesn’t remember earlier messages: the chat resends them each turn.'}
        </p>
      </SettingsSection>
      <SettingsSection icon={Variable} tint="text-tint-blue" title="Test values">
        {variables.length > 0 ? (
          <RequestContextPicker
            tenantId={tenantId}
            id="playground-context"
            expected={variables}
            onChange={setRequestContext}
          />
        ) : (
          <p className="text-muted-foreground text-xs">
            This version has no {'{{variables}}'} or session variables to fill.
          </p>
        )}
      </SettingsSection>
      <SettingsSection icon={Webhook} tint="text-tint-green" title="Request headers">
        {endpoint ? (
          <HeadersEditor
            expected={expected}
            enabled={testHeaders.enabled}
            rows={testHeaders.rows}
            onChange={testHeaders.set}
          />
        ) : (
          <p className="text-muted-foreground text-xs">
            Headers go through an endpoint&rsquo;s mapping. This agent has no endpoint yet; create
            one on the{' '}
            <Link to="/endpoints" className="underline">
              Endpoints
            </Link>{' '}
            page to test them here.
          </p>
        )}
      </SettingsSection>
    </div>
  );

  return (
    // Fill the page panel edge to edge, below its bar.
    <div className="-m-4 flex h-[calc(100dvh-3rem)] md:-m-5 md:h-[calc(100dvh-4rem)]">
      <div className="flex min-w-0 flex-1 flex-col">
        {!wide && (
          <div className="flex h-11 shrink-0 items-center justify-end border-b px-3">
            <Button variant="ghost" size="sm" onClick={() => setSheetOpen(true)}>
              <SlidersHorizontal aria-hidden />
              Test settings
            </Button>
          </div>
        )}
        <AgentChat
          key={version}
          className="flex-1"
          agentId={agent.id}
          agentName={agent.name}
          version={version}
          stateful={stateful}
          requestContext={requestContext}
          headers={endpoint ? testHeaders.headers : undefined}
        />
      </div>
      {wide ? (
        <aside
          aria-label="Test settings"
          className="bg-card/60 flex w-[22rem] shrink-0 flex-col border-l xl:w-[24rem]"
        >
          <h2 className="flex h-11 shrink-0 items-center border-b px-4 text-sm font-medium">
            Test settings
          </h2>
          <div className="min-h-0 flex-1 overflow-y-auto">{settings}</div>
        </aside>
      ) : (
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetContent className="w-full overflow-y-auto p-0 sm:max-w-md">
            <SheetHeader className="border-b">
              <SheetTitle>Test settings</SheetTitle>
              <SheetDescription>Sent with every message in this chat.</SheetDescription>
            </SheetHeader>
            {settings}
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}

function PlaygroundContent({ tenantId, role }: { tenantId: string; role: Role }) {
  const search = useSearch({ from: '/app/playground' });
  const navigate = useNavigate({ from: '/playground' });
  const agents = usePaged<AgentDto>(['agents', tenantId], '/agents');
  const list = agents.data?.pages.flatMap((p) => p.items) ?? [];
  const agent = list.find((a) => a.id === search.agentId) ?? (search.agentId ? undefined : list[0]);
  const [version, setVersion] = useState('draft');

  const versions = useQuery({
    queryKey: ['agent-versions', tenantId, agent?.id, agent?.publishedVersionId],
    queryFn: () => api<{ items: AgentVersionDto[] }>(`/agents/${agent!.id}/versions`),
    enabled: Boolean(agent),
  });
  useEffect(() => {
    setVersion('draft');
  }, [agent?.id]);
  const selectedVersion = versions.data?.items.find((v) =>
    version === 'draft'
      ? !v.frozen
      : version === 'published'
        ? v.id === agent?.publishedVersionId
        : v.id === version,
  );

  if (!hasRole(role, 'admin')) {
    return (
      <>
        <PageHeader title="Playground" />
        <EmptyState title="Admins only">
          The playground calls model providers, so it requires the admin role.
        </EmptyState>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Playground"
        actions={
          <>
            <Select
              value={agent?.id ?? ''}
              onValueChange={(id) => void navigate({ search: { agentId: id } })}
            >
              <SelectTrigger size="sm" aria-label="Agent" className="w-28 sm:w-48">
                <SelectValue placeholder="Choose an agent" />
              </SelectTrigger>
              <SelectContent>
                {list.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={version} onValueChange={setVersion} disabled={!agent}>
              <SelectTrigger size="sm" aria-label="Version" className="w-24 sm:w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                {agent?.publishedVersionId && (
                  <SelectItem value="published">Published (v{agent.publishedVersion})</SelectItem>
                )}
                {versions.data?.items
                  .filter((v) => v.frozen && v.id !== agent?.publishedVersionId)
                  .map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      v{v.version}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {agent && (
              <Button variant="outline" size="sm" className="max-sm:px-2" asChild>
                <Link to="/agents/$agentId" params={{ agentId: agent.id }} aria-label="Edit agent">
                  <Pencil className="sm:hidden" aria-hidden />
                  <span className="max-sm:hidden">Edit agent</span>
                </Link>
              </Button>
            )}
          </>
        }
      />
      <ErrorAlert error={agents.error ?? versions.error} />
      {!agents.isPending && list.length === 0 ? (
        <EmptyState title="No agents yet">
          <Link to="/agents" className="underline">
            Create an agent
          </Link>{' '}
          first.
        </EmptyState>
      ) : (
        agent && (
          <PlaygroundSession
            key={agent.id}
            tenantId={tenantId}
            agent={agent}
            version={version}
            selectedVersion={selectedVersion}
          />
        )
      )}
    </>
  );
}

export function PlaygroundPage() {
  return (
    <RequireTenant>
      {(tenant) => <PlaygroundContent tenantId={tenant.id} role={tenant.role} />}
    </RequireTenant>
  );
}
